import { ChangeDetectionStrategy, Component, ElementRef, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { FaIconLibrary } from '@fortawesome/angular-fontawesome';
import { IconName } from '@fortawesome/fontawesome-svg-core';
import { faCircle, faMicrophone } from '@fortawesome/free-solid-svg-icons';
import { BehaviorSubject, Observable, ReplaySubject, Subject } from 'rxjs';
import { map, take, takeUntil, withLatestFrom } from 'rxjs/operators';

import { AppFacade } from 'ish-core/facades/app.facade';
import { GoogleTagManagerFacade } from 'ish-core/facades/gtm.facade';
import { ShoppingFacade } from 'ish-core/facades/shopping.facade';
import { CategoryHelper } from 'ish-core/models/category/category.helper';
import { SearchBoxConfiguration } from 'ish-core/models/search-box-configuration/search-box-configuration.model';
import { CustomSuggestTerm } from 'ish-core/models/suggest-term/custom-suggest-term.model';
import { GenerateLazyComponent } from 'ish-core/utils/module-loader/generate-lazy-component.decorator';

interface SpeechRecognitionResult {
  transcript: string;
}

interface SpeechRecognitionEvent extends Event {
  results: SpeechRecognitionResult[][];
}

interface SpeechRecognitionErrorEvent extends Event {
  error: string;
  message: string;
}

interface WebkitSpeechRecognition extends EventTarget {
  new (): WebkitSpeechRecognition;
  start(): void;
  stop(): void;
  abort(): void;
  lang: string;
  interimResults: boolean;
  maxAlternatives: number;
  onresult: ((event: SpeechRecognitionEvent) => void) | null;
  onerror: ((event: SpeechRecognitionErrorEvent) => void) | null;
  onspeechend: (() => void) | null;
  onend: (() => void) | null;
}

@Component({
  selector: 'custom-search-box',
  templateUrl: './custom-search-box.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
@GenerateLazyComponent()
export class CustomSearchBoxComponent implements OnInit, OnDestroy {
  @Input() configuration: SearchBoxConfiguration;

  searchInput = '';
  searchResults$: Observable<CustomSuggestTerm[]>;
  cumulatedSearchResults$ = new ReplaySubject<CustomSuggestTerm[]>(1);
  inputSearchTerms$ = new ReplaySubject<string>(1);
  productSearchResults$ = new ReplaySubject<CustomSuggestTerm[]>(1);
  categorySearchResults$ = new ReplaySubject<CustomSuggestTerm[]>(1);
  brandSearchResults$ = new ReplaySubject<CustomSuggestTerm[]>(1);
  activeIndex = -1;
  inputFocused = false;
  cumulatedResults = 0;
  maxProductAutoSuggestions: number;
  maxCategoryAutoSuggestions = 5;
  maxBrandAutoSuggestions = 5;
  private destroy$ = new Subject<void>();

  @ViewChild('searchInput') searchIP!: ElementRef;

  searchQuery = '';
  errorMessage = '';
  isRecording$ = new BehaviorSubject<boolean>(false); // Observable for isRecording

  recognition: WebkitSpeechRecognition | undefined;
  private recordingTimeout: NodeJS.Timeout | undefined; // Variable to store the recording timeout

  constructor(
    private shoppingFacade: ShoppingFacade,
    private router: Router,
    library: FaIconLibrary,
    private appFacade: AppFacade,
    private gtmFacade: GoogleTagManagerFacade
  ) {
    if (!SSR) {
      library.addIcons(faMicrophone, faCircle);

      if ('webkitSpeechRecognition' in window) {
        this.recognition = new (
          window as Window & { webkitSpeechRecognition: new () => WebkitSpeechRecognition }
        ).webkitSpeechRecognition();
        this.recognition.lang = 'en-US';
        this.recognition.interimResults = false;
        this.recognition.maxAlternatives = 1;

        this.recognition.onresult = (event: SpeechRecognitionEvent) => {
          this.searchQuery = event.results[0][0].transcript;
          this.isRecording$.next(false);
          this.clearRecordingTimeout(); // Clear timeout after result

          this.inputSearchTerms$.next(this.searchQuery);
          this.triggerEnter();
          // Manually trigger change detection after receiving results
        };

        this.recognition.onerror = (event: SpeechRecognitionErrorEvent) => {
          this.errorMessage = event.error;
          this.isRecording$.next(false);
          this.clearRecordingTimeout(); // Clear timeout on error
        };

        this.recognition.onspeechend = () => {
          // Let the user stop speaking naturally
        };

        this.recognition.onend = () => {
          this.isRecording$.next(false);
          this.clearRecordingTimeout(); // Clear timeout when speech ends
        };
      } else {
        this.errorMessage = 'Your browser does not support voice search.';
      }
    }
  }

  get usedIcon(): IconName {
    return this.configuration?.icon || 'search';
  }

  ngOnInit() {
    this.maxProductAutoSuggestions = this.configuration?.maxAutoSuggests ?? 10;
    this.shoppingFacade.searchTerm$
      .pipe(
        map(x => x ?? ''),
        takeUntil(this.destroy$)
      )
      .subscribe(term => this.inputSearchTerms$.next(term));

    this.searchResults$ = this.shoppingFacade.customSearchResults$(this.inputSearchTerms$);

    this.searchResults$.pipe(takeUntil(this.destroy$)).subscribe(results => {
      if (results) {
        const productResults = results
          .filter(result => result.type === 'product')
          .slice(0, this.maxProductAutoSuggestions);
        const categoryResults = results
          .filter(result => result.type === 'category')
          .slice(0, this.maxCategoryAutoSuggestions);
        const brandResults = results.filter(result => result.type === 'brand').slice(0, this.maxBrandAutoSuggestions);

        this.productSearchResults$.next(productResults);
        this.categorySearchResults$.next(categoryResults);
        this.brandSearchResults$.next(brandResults);
        this.cumulatedSearchResults$.next([...productResults, ...categoryResults, ...brandResults]);

        this.cumulatedResults = productResults?.length + categoryResults?.length + brandResults?.length;
      }
    });
  }

  triggerEnter() {
    const eve = new KeyboardEvent('keydown', {
      key: 'Enter',
      code: 'Enter',
      keyCode: 13,
      charCode: 13,
      bubbles: true,
      cancelable: true,
    });

    // Dispatch the event on the input element
    this.searchIP.nativeElement.dispatchEvent(eve);
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
    this.clearRecordingTimeout(); // Ensure timeout is cleared on destroy
  }

  blur() {
    this.inputFocused = false;
    this.activeIndex = -1;
  }

  focus() {
    this.inputFocused = true;
  }

  submitSearch(searchTerm: string) {
    if (!searchTerm) {
      return false;
    }

    // remove focus when switching to search page
    this.inputFocused = false;

    if (this.activeIndex !== -1) {
      // something was selected via keyboard
      this.cumulatedSearchResults$
        .pipe(withLatestFrom(this.inputSearchTerms$), take(1), takeUntil(this.destroy$))
        .subscribe(([results, inputSearchTerm]) => {
          switch (results[this.activeIndex].type) {
            case 'product': {
              this.gtmFacade.triggerGTMEvent(
                '',
                `${inputSearchTerm},${results[this.activeIndex].term},${results[this.activeIndex].type}`
              );
              this.gtmFacade.triggerGTMEvent('view_item', [{ sku: results[this.activeIndex].id }]);
              this.router.navigate(['/p', results[this.activeIndex].id]);
              break;
            }
            case 'category': {
              this.gtmFacade.triggerGTMEvent(
                '',
                `${inputSearchTerm},${results[this.activeIndex].term},${results[this.activeIndex].type}`
              );
              this.router.navigate([
                '/c',
                results[this.activeIndex].id.replace(new RegExp(`\/`, 'g'), CategoryHelper.uniqueIdSeparator),
              ]);
              break;
            }
            case 'brand':
              {
                this.gtmFacade.triggerGTMEvent(
                  '',
                  `${inputSearchTerm},${results[this.activeIndex].term},${results[this.activeIndex].type}`
                );
                this.router.navigate(['/search', results[this.activeIndex].id]);
                // No default
              }
              break;
          }
        });
    } else {
      this.gtmFacade.triggerGTMEvent('', searchTerm);
      this.router.navigate(['/search', searchTerm]);
    }

    // prevent form submission
    return false;
  }

  searchSuggest(event: Event) {
    const target = event.target as HTMLInputElement;
    this.inputSearchTerms$.next(target.value);
  }

  searchSuggestEmpty() {
    this.inputSearchTerms$.next('');
    this.cumulatedSearchResults$.next([]);
    this.productSearchResults$.next([]);
    this.categorySearchResults$.next([]);
    this.brandSearchResults$.next([]);
  }

  setSearchActiveStatus(focused: boolean) {
    this.inputFocused = focused;
    this.appFacade.setSeachActive(focused);
  }

  startVoiceSearch() {
    if (this.recognition) {
      this.isRecording$
        .pipe(
          take(1), // Only take the first emitted value
          takeUntil(this.destroy$)
        )
        .subscribe(isRecording => {
          if (isRecording) {
            this.recognition.stop();
            this.isRecording$.next(false);
            this.clearRecordingTimeout(); // Stop the timeout if recording is stopped
          } else {
            this.isRecording$.next(true);
            this.recognition.start();
            this.setRecordingTimeout(); // Start the timeout when recording starts
          }
        });
    } else {
      alert('Voice search is not supported in your browser.');
    }
  }

  private setRecordingTimeout() {
    this.clearRecordingTimeout(); // Clear any previous timeouts
    this.recordingTimeout = setTimeout(() => {
      this.isRecording$
        .pipe(
          take(1), // Only take the first emitted value
          takeUntil(this.destroy$)
        )
        .subscribe(isRecording => {
          if (!isRecording) {
            this.isRecording$.next(false);
          }
        });
    }, 5000); // Set timeout for 5 seconds, only if not recording
  }

  private clearRecordingTimeout() {
    if (this.recordingTimeout !== undefined) {
      clearTimeout(this.recordingTimeout);
      this.recordingTimeout = undefined;
    }
  }

  changeActiveIndex(index: number) {
    this.activeIndex = index;
  }

  selectSuggestedTerm(index: number) {
    this.productSearchResults$.pipe(takeUntil(this.destroy$)).subscribe(cumulatedResults => {
      if (cumulatedResults?.length) {
        this.activeIndex = index < 0 ? cumulatedResults.length - 1 : index % cumulatedResults.length;
      }
    });
  }
}
